// src/CsvExport.js - Page dédiée pour l'export CSV des cours des métaux
import React, { useState } from 'react';
import './styles/CsvExport.css';

const CsvExport = () => {
  // État pour l'export CSV
  const [csvExport, setCsvExport] = useState({
    symbol: 'XAU',
    startDate: '',
    endDate: '',
    range: 'daily',
    fields: ['mid'],
    unit: 'oz',
    isExporting: false
  });

  // Handler pour l'export CSV
  const handleExportCSV = async () => {
    const { symbol, startDate, endDate, range, fields, unit } = csvExport;

    // Validation
    if (!startDate || !endDate) {
      alert('Veuillez sélectionner une date de début et une date de fin');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      alert('La date de début doit être antérieure à la date de fin');
      return;
    }

    try {
      setCsvExport(prev => ({ ...prev, isExporting: true }));

      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        symbol,
        start: startDate,
        end: endDate,
        range,
        fields: fields.join(','),
        unit
      });

      const response = await fetch(`/api/metals/export-csv?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur lors de l\'export');
      }

      // Télécharger le fichier
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `metal_${symbol.toLowerCase()}_${startDate}_${endDate}_${range}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Export CSV réussi !');
    } catch (error) {
      console.error('Erreur lors de l\'export CSV:', error);
      alert(`Erreur: ${error.message}`);
    } finally {
      setCsvExport(prev => ({ ...prev, isExporting: false }));
    }
  };

  // Définir des dates par défaut intelligentes
  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setCsvExport(prev => ({
      ...prev,
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    }));
  };

  return (
    <div className="csv-export-page">
      <div className="page-container">
        {/* Header */}
        <div className="page-header glass-header">
          <div className="header-content">
            <div className="header-text">
              <h1 className="page-title">
                <span className="title-icon">📥</span>
                Export CSV
              </h1>
              <p className="page-subtitle">
                Exportez l'historique des cours des métaux précieux au format CSV
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="export-container">
          {/* Export Form Card */}
          <div className="card glass-card export-card">
            <div className="card-header">
              <h2 className="card-title">
                <span className="card-icon">⚙️</span>
                Configuration de l'export
              </h2>
            </div>
            <div className="card-body">
              {/* Quick Range Buttons */}
              <div className="quick-range-section">
                <label className="section-label">Périodes rapides</label>
                <div className="quick-range-buttons">
                  <button
                    className="btn btn-outline quick-btn"
                    onClick={() => setQuickRange(7)}
                  >
                    7 derniers jours
                  </button>
                  <button
                    className="btn btn-outline quick-btn"
                    onClick={() => setQuickRange(30)}
                  >
                    30 derniers jours
                  </button>
                  <button
                    className="btn btn-outline quick-btn"
                    onClick={() => setQuickRange(90)}
                  >
                    3 derniers mois
                  </button>
                  <button
                    className="btn btn-outline quick-btn"
                    onClick={() => setQuickRange(365)}
                  >
                    1 an
                  </button>
                </div>
              </div>

              <div className="divider"></div>

              {/* Export Form */}
              <div className="csv-export-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="metal-select" className="form-label">
                      <span className="label-icon">🥇</span>
                      Métal
                    </label>
                    <select
                      id="metal-select"
                      className="form-select modern-input"
                      value={csvExport.symbol}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, symbol: e.target.value }))}
                    >
                      <option value="XAU">🥇 Or (Gold)</option>
                      <option value="XAG">🥈 Argent (Silver)</option>
                      <option value="XPT">💎 Platine (Platinum)</option>
                      <option value="XPD">⚡ Palladium</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="start-date" className="form-label">
                      <span className="label-icon">📅</span>
                      Date de début
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      className="form-input modern-input"
                      value={csvExport.startDate}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="end-date" className="form-label">
                      <span className="label-icon">📅</span>
                      Date de fin
                    </label>
                    <input
                      type="date"
                      id="end-date"
                      className="form-input modern-input"
                      value={csvExport.endDate}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="range-select" className="form-label">
                      <span className="label-icon">⏱️</span>
                      Intervalle
                    </label>
                    <select
                      id="range-select"
                      className="form-select modern-input"
                      value={csvExport.range}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, range: e.target.value }))}
                    >
                      <option value="raw">Données brutes (toutes les entrées)</option>
                      <option value="hourly">Horaire (moyenne par heure)</option>
                      <option value="daily">Journalier (moyenne par jour)</option>
                      <option value="weekly">Hebdomadaire (moyenne par semaine)</option>
                      <option value="monthly">Mensuel (moyenne par mois)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="unit-select" className="form-label">
                      <span className="label-icon">⚖️</span>
                      Unité de prix
                    </label>
                    <select
                      id="unit-select"
                      className="form-select modern-input"
                      value={csvExport.unit}
                      onChange={(e) => setCsvExport(prev => ({ ...prev, unit: e.target.value }))}
                    >
                      <option value="oz">Once Troy (€/oz)</option>
                      <option value="gram">Gramme (€/g)</option>
                      <option value="kg">Kilogramme (€/kg)</option>
                    </select>
                  </div>
                </div>

                {/* Fields Selection */}
                <div className="fields-selection-section">
                  <label className="section-label">
                    <span className="label-icon">📊</span>
                    Données à exporter
                  </label>
                  <div className="fields-checkboxes">
                    {[
                      { value: 'mid', label: 'Mid (Prix moyen)', description: 'Prix moyen bid/ask' },
                      { value: 'bid', label: 'Bid (Achat)', description: 'Prix d\'achat' },
                      { value: 'ask', label: 'Ask (Vente)', description: 'Prix de vente' },
                      { value: 'open', label: 'Open (Ouverture)', description: 'Prix d\'ouverture' },
                      { value: 'high', label: 'High (Plus haut)', description: 'Prix le plus haut' },
                      { value: 'low', label: 'Low (Plus bas)', description: 'Prix le plus bas' },
                      { value: 'close', label: 'Close (Clôture)', description: 'Prix de clôture' },
                      { value: 'spread', label: 'Spread (Écart)', description: 'Écart bid-ask' },
                      { value: 'average', label: 'Average (Moyenne)', description: 'Prix moyen' }
                    ].map(field => (
                      <label key={field.value} className="checkbox-field">
                        <input
                          type="checkbox"
                          checked={csvExport.fields.includes(field.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCsvExport(prev => ({ ...prev, fields: [...prev.fields, field.value] }));
                            } else {
                              setCsvExport(prev => ({ ...prev, fields: prev.fields.filter(f => f !== field.value) }));
                            }
                          }}
                        />
                        <div className="checkbox-content">
                          <span className="checkbox-label">{field.label}</span>
                          <span className="checkbox-description">{field.description}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {csvExport.fields.length === 0 && (
                    <p className="field-warning">⚠️ Veuillez sélectionner au moins un champ à exporter</p>
                  )}
                </div>

                {/* Export Button */}
                <div className="export-actions">
                  <button
                    className="btn btn-primary btn-export-large"
                    onClick={handleExportCSV}
                    disabled={csvExport.isExporting || !csvExport.startDate || !csvExport.endDate || csvExport.fields.length === 0}
                  >
                    <span className="btn-icon">
                      {csvExport.isExporting ? '⏳' : '📥'}
                    </span>
                    {csvExport.isExporting ? 'Export en cours...' : 'Télécharger le fichier CSV'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Example Preview */}
          <div className="card glass-card preview-card">
            <div className="card-header">
              <h2 className="card-title">
                <span className="card-icon">👁️</span>
                Aperçu du format CSV
              </h2>
            </div>
            <div className="card-body">
              <div className="csv-preview">
                <pre className="csv-content">
{`timestamp,mid (€/oz),bid (€/oz),ask (€/oz)
2024-01-01 00:00:00,1850.50,1848.30,1852.70
2024-01-02 00:00:00,1852.30,1850.10,1854.50
2024-01-03 00:00:00,1848.75,1846.55,1850.95
...`}
                </pre>
              </div>
              <p className="preview-note">
                <span className="info-icon">💡</span>
                Les données exportées peuvent être ouvertes avec Excel, Google Sheets ou tout autre logiciel supportant le format CSV.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CsvExport;

